<template>
  <div data-testid="approvalsTab-container-root">
    <RoutineApprovalEmptyState
      v-if="statusInProgress"
      data-testid="approvalsTab-emptyState-inProgress"
      scenario="inProgress"
    />

    <div v-else>
      <v-sheet
        v-if="isLoadingApprovals"
        class="px-6 py-8 d-flex justify-center"
        data-testid="approvalsTab-state-loading"
        outlined
        rounded
      >
        <v-progress-circular color="primary" indeterminate />
      </v-sheet>

      <RoutineApprovalEmptyState
        v-if="!isLoadingApprovals && shouldShowEmptyState"
        data-testid="approvalsTab-emptyState-noApprovals"
        scenario="noApprovals"
        :canRequestApproval="routinePermissions.requestApproval"
        @requestApproval="openRequestDialog"
      />

      <template v-if="!isLoadingApprovals && !shouldShowEmptyState">
        <div
          v-if="routinePermissions.requestApproval"
          class="d-flex align-center justify-space-between mb-4"
          data-testid="approvalsTab-header-actions"
        >
          <Button
            color="primary"
            data-testid="approvalsTab-button-requestApproval"
            icon="mdi-clipboard-search-outline"
            outlined
            :disabled="
              isLoadingApprovals || !routinePermissions.requestApproval
            "
            :size="SIZES.small"
            :text="$t('routines.approval.request_approval')"
            @click="openRequestDialog"
          />
        </div>

        <v-row class="ma-0">
          <v-col class="pa-0 pe-2" cols="6">
            <ApprovalsList
              :approvals="sortedApprovals"
              :selectedApprovalId="selectedApprovalId"
              @select="handleApprovalSelected"
            />
          </v-col>

          <v-col cols="6" class="pa-0 ps-2">
            <v-card v-if="selectedApproval" outlined rounded>
              <ApprovalDetail
                :approval="selectedApproval"
                :isCancelling="isCancellingApproval"
                :isReRequesting="isReRequestingApproval"
                :routine="routine"
                @approveRequest="openApprovalDialog"
                @cancelRequest="openCancelRequestConfirmation"
                @rejectRequest="openRejectDialog"
                @reRequest="handleReRequestClick"
                @updated="handleApprovalUpdated"
              />
            </v-card>
          </v-col>
        </v-row>
      </template>

      <RoutineApprovalRequestDialog
        v-if="isRequestDialogOpen"
        data-testid="approvalsTab-dialog-requestApproval"
        :blacklist="requestedApproverBlacklist"
        :remainingRequests="remainingRequests"
        :routineId="routine.id"
        :show="isRequestDialogOpen"
        @approved="handleRequestApproved"
        @close="closeRequestDialog"
      />

      <RoutineApprovalDialog
        v-if="isApprovalDialogOpen"
        data-testid="approvalsTab-dialog-approveRequest"
        shouldApproveRequest
        :approval="selectedApproval"
        :routine="routine"
        :routineId="routine.id"
        :show="isApprovalDialogOpen"
        @approved="handleApprovalUpdated"
        @close="closeApprovalDialog"
        @updated="handleApprovalUpdated"
      />

      <RejectApprovalDialog
        v-if="isRejectDialogOpen && selectedApproval"
        data-testid="approvalsTab-dialog-rejectRequest"
        :approval="selectedApproval"
        :routineId="routine.id"
        :show="isRejectDialogOpen"
        @close="closeRejectDialog"
        @rejected="handleApprovalRejected"
      />

      <DeleteConfirmationModal
        :cancelText="$t('common.button.close')"
        :confirmText="$t('routines.approval.cancel_request')"
        :isLoading="isCancellingApproval"
        :show="showCancelRequestConfirmation"
        :text="$t('routines.approval.cancel_request_message')"
        :title="$t('routines.approval.cancel_request')"
        @cancel="closeCancelRequestConfirmation"
        @delete="handleCancelSelectedApproval"
      />
    </div>
  </div>
</template>

<script>
import _ from 'lodash'
import { mapGetters } from 'vuex'
import { utils } from 'frontend-js-shared'

import api from '@/api'
import eventHub, { HUB_EVENTS } from '@/lib/event-hub'
import Button, { SIZES } from '@/components/UI/Button.vue'
import DeleteConfirmationModal from '@/components/Modals/DeleteConfirmationModal.vue'
import RoutineApprovalDialog from '@/components/Routines/RoutineApproval/RoutineApprovalDialog.vue'
import RoutineApprovalRequestDialog from '@/components/Routines/RoutineApproval/RoutineApprovalRequestDialog.vue'
import RejectApprovalDialog from '@/components/Routines/RoutineApproval/RejectApprovalDialog.vue'
import mixin from '@/mixins/mixin.vue'
import routinePermissions from '@/mixins/routinePermissions.vue'

import ApprovalDetail from './ApprovalDetail.vue'
import ApprovalsList from './ApprovalsList.vue'
import RoutineApprovalEmptyState from './RoutineApprovalEmptyState.vue'

const MAX_ROUTINE_APPROVAL_REQUESTS = 10

export default {
  name: 'ApprovalsTab',

  components: {
    ApprovalDetail,
    ApprovalsList,
    Button,
    DeleteConfirmationModal,
    RejectApprovalDialog,
    RoutineApprovalDialog,
    RoutineApprovalRequestDialog,
    RoutineApprovalEmptyState,
  },

  mixins: [mixin, routinePermissions],

  props: {
    routine: {
      type: Object,
      required: true,
    },
    routineDefinition: {
      type: Object,
      default: () => ({}),
    },
  },

  data() {
    return {
      approvals: [],
      isApprovalDialogOpen: false,
      isCancellingApproval: false,
      isLoadingApprovals: true,
      isRejectDialogOpen: false,
      isReRequestingApproval: false,
      isRequestDialogOpen: false,
      selectedApprovalId: null,
      showCancelRequestConfirmation: false,
      SIZES,
    }
  },

  computed: {
    ...mapGetters(['account', 'organizationId']),

    routinePermissions() {
      return this.getRoutinePermissions({
        approvals: this.approvals,
        routine: this.routine,
        routineDefinition: this.routineDefinition,
      })
    },

    remainingRequests() {
      return Math.max(0, MAX_ROUTINE_APPROVAL_REQUESTS - this.approvals.length)
    },

    sortedApprovals() {
      return _.orderBy(this.approvals, ['createdAt'], ['desc'])
    },

    selectedApproval() {
      if (!this.selectedApprovalId) {
        return null
      }

      return (
        this.approvals.find(
          approval => approval.id === this.selectedApprovalId
        ) || null
      )
    },

    shouldShowEmptyState() {
      return _.isEmpty(this.approvals)
    },

    statusInProgress() {
      return this.routine?.status !== 'completed'
    },

    requestedApproverBlacklist() {
      const approverIds = this.approvals
        .map(approval => utils.getEntityId(approval?.approver))
        .filter(Boolean)

      return [...new Set([...approverIds, this.account.id])]
    },
  },

  mounted() {
    this.fetchApprovals()
    eventHub.$on(
      HUB_EVENTS.ROUTINE_APPROVAL.UPDATED,
      this.handleRoutineApprovalUpdated
    )
  },

  beforeDestroy() {
    eventHub.$off(
      HUB_EVENTS.ROUTINE_APPROVAL.UPDATED,
      this.handleRoutineApprovalUpdated
    )
  },

  methods: {
    closeRequestDialog() {
      this.isRequestDialogOpen = false
    },

    handleRoutineApprovalUpdated(payload) {
      if (payload?.routineId && payload.routineId !== this.routine.id) {
        return
      }

      this.fetchApprovals()
    },

    closeApprovalDialog() {
      this.isApprovalDialogOpen = false
    },

    closeCancelRequestConfirmation() {
      this.showCancelRequestConfirmation = false
    },

    async fetchApprovals() {
      if (this.statusInProgress) return

      this.isLoadingApprovals = true

      try {
        const { data } = await api.routine.getRoutineApprovals(
          this.organizationId,
          this.routine.id,
          {
            relations: [
              'createdBy',
              'createdBy.photo',
              'approver',
              'approver.photo',
            ],
          }
        )

        this.approvals = data?.results || []
        this.syncSelectedApproval()
      } catch (error) {
        this.approvals = []
        this.selectedApprovalId = null
        this.handleRequestError(error)
      } finally {
        this.isLoadingApprovals = false
      }
    },

    async handleRequestApproved() {
      await this.fetchApprovals()
    },

    handleApprovalSelected(approval) {
      this.selectedApprovalId = approval?.id || null
    },

    syncSelectedApproval() {
      if (!this.selectedApprovalId) {
        return
      }

      const selectedApproval =
        this.approvals.find(
          approval => approval.id === this.selectedApprovalId
        ) || null

      if (!selectedApproval) {
        this.selectedApprovalId = null
      }
    },

    async handleCancelSelectedApproval() {
      if (!this.selectedApprovalId) {
        return
      }

      try {
        this.isCancellingApproval = true

        await api.routine.removeRoutineApproval(
          this.organizationId,
          this.routine.id,
          this.selectedApprovalId
        )

        this.closeCancelRequestConfirmation()
        this.selectedApprovalId = null
        await this.fetchApprovals()
      } catch (error) {
        this.handleRequestError(error)
      } finally {
        this.isCancellingApproval = false
      }
    },

    openRequestDialog() {
      if (this.isLoadingApprovals || !this.routinePermissions.requestApproval) {
        return
      }

      this.isRequestDialogOpen = true
    },

    openApprovalDialog() {
      if (!this.selectedApproval) {
        return
      }

      this.isApprovalDialogOpen = true
    },

    async handleApprovalUpdated(updatedApproval) {
      this.closeApprovalDialog()

      if (updatedApproval?.id) {
        this.selectedApprovalId = updatedApproval.id
      }

      await this.fetchApprovals()
    },

    openCancelRequestConfirmation() {
      this.showCancelRequestConfirmation = true
    },

    openRejectDialog() {
      if (!this.selectedApproval) {
        return
      }

      this.isRejectDialogOpen = true
    },

    closeRejectDialog() {
      this.isRejectDialogOpen = false
    },

    async handleApprovalRejected() {
      this.closeRejectDialog()
      await this.fetchApprovals()
    },

    handleReRequestClick() {
      if (!this.selectedApproval) {
        return
      }

      this.submitReRequest(this.selectedApproval)
    },

    async submitReRequest(approval) {
      if (!approval?.id) {
        return
      }

      this.isReRequestingApproval = true

      try {
        await api.routine.updateRoutineApproval(
          this.organizationId,
          this.routine.id,
          approval.id,
          { status: 'requested' }
        )

        this.showSuccess(this.$t('routines.approval.re_request_success'))
        await this.fetchApprovals()
      } catch (error) {
        this.showError(this.$t('routines.approval.re_request_failed'))
        this.handleRequestError(error)
      } finally {
        this.isReRequestingApproval = false
      }
    },
  },
}
</script>
